import React, { useRef, useState, useEffect } from 'react';
import { Box, TextField, Autocomplete, Tooltip } from '@mui/material';
import stockTransferStyles from '../StockTransfer.module.css';

interface QtyUnitInputProps {
  qty?: number | '';
  qtyUnit?: string;
  onQtyChange: (qty: number | '') => void;
  onQtyUnitChange: (unit: string) => void;
  unitOptions?: string[];
  hasError?: boolean; // when true, show red border
  disabled?: boolean; // when true, disable inputs and show not-allowed cursor
  isDisableDropdown?: boolean; // when true, disable unit dropdown
}

// Component for rendering option with conditional tooltip (only when text overflows)
const OverflowOption: React.FC<{ option: string; props: React.HTMLAttributes<HTMLLIElement> }> = ({ option, props }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [option]);

  return (
    <Tooltip title={isOverflowing ? option : ''} placement="right" arrow disableHoverListener={!isOverflowing}>
      <li {...props}>
        <span
          ref={textRef}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            width: '100%',
          }}
        >
          {option}
        </span>
      </li>
    </Tooltip>
  );
};

const QtyUnitInput: React.FC<QtyUnitInputProps> = ({
  qty,
  qtyUnit,
  onQtyChange,
  onQtyUnitChange,
  unitOptions = [],
  hasError = false,
  disabled = false,
  isDisableDropdown = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputOverflowing, setIsInputOverflowing] = useState(false);

  // Check if the selected value in the input is overflowing
  useEffect(() => {
    const checkOverflow = () => {
      const el = inputRef.current;
      if (el) {
        setIsInputOverflowing(el.scrollWidth > el.clientWidth);
      }
    };
    checkOverflow();
  }, [qtyUnit]);

  return (
    <Box
      className={`${stockTransferStyles.qtyUnitInputContainer} ${hasError ? stockTransferStyles.qtyUnitInputContainerError : ''} ${disabled ? stockTransferStyles.qtyUnitInputContainerDisabled : ''}`}
    >
      {/* Qty Input */}
      <TextField
        type="number"
        disabled={disabled}
        value={qty ?? ''}
        onChange={e => {
          const raw = e.target.value;
          if (raw === '') {
            onQtyChange('');
            return;
          }
          const val = Number(raw);
          // Only allow integers, prevent decimals
          if (Number.isNaN(val) || !Number.isInteger(val)) {
            return;
          }
          onQtyChange(Math.max(0, val));
        }}
        onKeyDown={e => {
          // Prevent decimal point, minus, and plus keys
          if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
          }
        }}
        inputProps={{ 
          min: 0,
          step: 1
        }}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: { fontSize: 14, width: '65px' },
        }}
      />

      {/* Divider */}
      <Box
        sx={{
          width: '1px',
          height: '22px',
          bgcolor: '#ccc',
          mx: 1,
        }}
      />
   
      {/* Unit Dropdown with Tooltip only when text overflows */}
      <Tooltip 
        title={isInputOverflowing ? (qtyUnit || '') : ''} 
        placement="top" 
        arrow 
        disableHoverListener={!isInputOverflowing}
      >
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            options={unitOptions}
            getOptionLabel={(option) => option}
            disabled={disabled || isDisableDropdown}
            value={qtyUnit || ''}
            onChange={(_, val) => onQtyUnitChange(val || '')}
            renderOption={(props, option) => (
              <OverflowOption key={option} option={option} props={props} />
            )}
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                inputRef={inputRef}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  sx: { 
                    fontSize: 13, 
                    minWidth: '110px',
                    '& input': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                  },
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-endAdornment': { right: 0 },
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default QtyUnitInput;
